import React from 'react';
import {
  ArrowLeft, Check, MessageSquare, FileText, ExternalLink, GitBranch,
  Sparkles, Terminal, Building2, ListChecks, CalendarCheck, Search, Briefcase, RefreshCw
} from 'lucide-react';
import AIPlacementRecommendationCard from './AIPlacementRecommendationCard';
import FormattedResumeSummary from './FormattedResumeSummary';
import CandidateRankingPanel from './tpo/CandidateRankingPanel';
import { SubFeatureGate } from './FeatureGate';
import api from '../api';

const AppliedCandidatesModule = ({
  appliedCandidates,
  selectedApplication,
  setSelectedApplication,
  selectedAppliedCompany,
  setSelectedAppliedCompany,
  selectedAppliedRole,
  setSelectedAppliedRole,
  loadingApplied,
  applicationsViewMode,
  setApplicationsViewMode,
  candidateSearch,
  setCandidateSearch,
  candidateCompanyFilter,
  setCandidateCompanyFilter,
  candidateRoleFilter,
  setCandidateRoleFilter,
  candidateStatusFilter,
  setCandidateStatusFilter,
  handleUpdateAppStatus,
  openRemarksModal,
  getStatusStyle,
  onBackToDrives,
  handleSyncApplications,
  syncingApplications
}) => {
  const formatStatusLabel = (status) => String(status || 'Applied').replace(/_/g, ' ');

  return (
              <div className="tpo-applied-candidates">
                {selectedApplication ? (
                  <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid var(--faculty-border)', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', borderBottom: '1px solid var(--faculty-border)', paddingBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <button
                          onClick={() => setSelectedApplication(null)}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--faculty-border)', background: 'var(--faculty-bg)', color: 'var(--faculty-text)', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}
                        >
                          <ArrowLeft size={16} /> Back to List
                        </button>
                        <div>
                          <h2 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0, color: 'var(--faculty-text)' }}>{selectedApplication.student_name}</h2>
                          <div style={{ fontSize: '0.88rem', color: 'var(--faculty-muted)', marginTop: '4px' }}>Applied for: <strong style={{ color: 'var(--faculty-accent)' }}>{selectedApplication.job_name}</strong> • {selectedApplication.email}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                          padding: '6px 14px',
                          borderRadius: '20px',
                          fontWeight: 700,
                          fontSize: '0.82rem',
                          ...getStatusStyle(selectedApplication.status)
                        }}>
                          Status: {formatStatusLabel(selectedApplication.status)}
                        </span>
                        {(!selectedApplication.status || !['APPROVED', 'UNDER REVIEW', 'UNDER_REVIEW', 'SHORTLISTED', 'HIRED', 'REJECTED'].includes(selectedApplication.status.toUpperCase())) && (
                          <button
                            onClick={() => handleUpdateAppStatus(selectedApplication.id, 'APPROVED')}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' }}
                          >
                            <Check size={18} /> Approve Application
                          </button>
                        )}
                        {(!selectedApplication.status || !['APPROVED', 'APPROVED.', 'REMARKED', 'UNDER REVIEW', 'UNDER_REVIEW', 'SHORTLISTED', 'HIRED', 'REJECTED'].includes(selectedApplication.status.toUpperCase())) && (
                          <button
                            onClick={() => openRemarksModal(selectedApplication)}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '8px', border: '1px solid #bfdbfe', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}
                          >
                            <MessageSquare size={18} /> Add Remarks
                          </button>
                        )}
                      </div>
                    </div>

                    {selectedApplication.remarks && (
                      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <MessageSquare size={16} /> Latest Remarks
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#b45309' }}>
                            Added by {selectedApplication.remarks_added_by || 'TPO'} • {selectedApplication.remarks_added_at || 'Recently'}
                          </span>
                        </div>
                        <p style={{ margin: 0, color: '#78350f', fontSize: '0.9rem', lineHeight: 1.5 }}>
                          {selectedApplication.remarks}
                        </p>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
                      <div style={{ background: 'var(--faculty-bg)', borderRadius: '14px', padding: '24px', border: '1px solid var(--faculty-border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', color: 'var(--faculty-text)' }}>
                          <FileText size={22} style={{ color: 'var(--faculty-accent)' }} />
                          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Resume & Academic Details</h3>
                        </div>
                        <div style={{ marginBottom: '16px', fontSize: '0.9rem', color: 'var(--faculty-text)', lineHeight: 1.6 }}>
                          <p style={{ margin: '0 0 8px 0' }}><strong>Batch:</strong> {selectedApplication.batch}</p>
                          <p style={{ margin: '0 0 8px 0' }}><strong>Department / Branch:</strong> {selectedApplication.branch}</p>
                          <p style={{ margin: '0 0 8px 0' }}><strong>Applied Date:</strong> {selectedApplication.applied_at}</p>
                        </div>
                        {selectedApplication.resume_url ? (
                          <div style={{ marginTop: '16px' }}>
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                const url = selectedApplication.resume_url;
                                if (url.startsWith('http')) {
                                  window.open(url, '_blank');
                                  return;
                                }
                                try {
                                  const cleanPath = url.replace('/api/documents/', '').split('/').filter(Boolean).join('/');
                                  const res = await api.get(`/api/documents/${cleanPath}`, { responseType: 'blob' });
                                  const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                  const a = document.createElement('a');
                                  a.href = blobUrl;
                                  a.target = '_blank';
                                  a.click();
                                  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                } catch (error) {
                                  console.error('Error fetching resume:', error);
                                  alert('Failed to load resume document.');
                                }
                              }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: 'var(--faculty-accent)', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                            >
                              <ExternalLink size={16} /> View Student Resume (PDF)
                            </button>
                          </div>
                        ) : (
                          <div style={{ padding: '12px', background: '#fff', borderRadius: '8px', border: '1px dashed var(--faculty-border)', color: 'var(--faculty-muted)', fontSize: '0.85rem' }}>
                            No uploaded resume PDF found.
                          </div>
                        )}
                        {(selectedApplication.resume_summary || selectedApplication.resume_text || selectedApplication.id) && (
                          <div style={{ marginTop: '16px' }}>
                            <FormattedResumeSummary 
                              summaryText={selectedApplication.resume_summary || selectedApplication.resume_text || "No resume summary available."} 
                              title="AI Resume Summary & Competency Breakdown"
                              application={selectedApplication}
                            />
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <AIPlacementRecommendationCard
                          application={selectedApplication}
                          onUpdateApplication={(updatedFields) => {
                            setSelectedApplication(prev => prev ? { ...prev, ...updatedFields } : prev);
                          }}
                          onApprove={() => handleUpdateAppStatus(selectedApplication.id, 'APPROVED')}
                          onRemark={() => openRemarksModal(selectedApplication)}
                        />

                        <div style={{ background: 'var(--faculty-bg)', borderRadius: '14px', padding: '24px', border: '1px solid var(--faculty-border)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', color: 'var(--faculty-text)' }}>
                            <GitBranch size={22} style={{ color: '#8b5cf6' }} />
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>GitHub Profile & Repositories</h3>
                          </div>
                          {selectedApplication.github_url && (
                            <div style={{ marginBottom: '16px' }}>
                              <a
                                href={
                                  selectedApplication.github_url.startsWith('http')
                                    ? selectedApplication.github_url
                                    : selectedApplication.github_url.includes('github.com')
                                    ? `https://${selectedApplication.github_url}`
                                    : `https://github.com/${selectedApplication.github_url.replace(/^\/+/, '')}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#24292e', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 600, fontSize: '0.88rem' }}
                              >
                                <ExternalLink size={16} /> Visit GitHub Profile
                              </a>
                            </div>
                          )}
                          {Array.isArray(selectedApplication.github_projects) && selectedApplication.github_projects.length > 0 && (
                            <div>
                              <strong style={{ display: 'block', fontSize: '0.88rem', color: 'var(--faculty-muted)', marginBottom: '10px' }}>Analyzed Projects:</strong>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '240px', overflowY: 'auto' }}>
                                {selectedApplication.github_projects.map((proj, idx) => {
                                  const projUrl = proj.url || proj.repoUrl || proj.html_url || proj.liveUrl || '';
                                  return (
                                    <div key={idx} style={{ padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid var(--faculty-border)' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                        <div style={{ fontWeight: 600, color: 'var(--faculty-text)', fontSize: '0.9rem' }}>
                                          {proj.name || proj.title || proj.repo || `Project #${idx+1}`}
                                        </div>
                                        {projUrl && (
                                          <a
                                            href={projUrl.startsWith('http') ? projUrl : `https://${projUrl}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#8b5cf6', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.78rem', fontWeight: 600, flexShrink: 0 }}
                                          >
                                            <ExternalLink size={13} /> View Repo
                                          </a>
                                        )}
                                      </div>
                                      {proj.description && <div style={{ fontSize: '0.82rem', color: 'var(--faculty-muted)', marginTop: '4px', lineHeight: 1.4 }}>{proj.description}</div>}
                                      {proj.language && <span style={{ display: 'inline-block', marginTop: '6px', fontSize: '0.75rem', background: 'var(--faculty-bg)', color: 'var(--faculty-text)', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{proj.language}</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {(() => {
                      let skillsList = [];
                      if (Array.isArray(selectedApplication.ai_skillset) && selectedApplication.ai_skillset.length > 0) {
                        skillsList = selectedApplication.ai_skillset.map(s => typeof s === 'object' ? (s.name || s.skill || JSON.stringify(s)) : s);
                      } else if (selectedApplication.skills) {
                        skillsList = typeof selectedApplication.skills === 'string' ? selectedApplication.skills.split(',').map(s => s.trim()).filter(Boolean) : [];
                      }

                      let toolsList = [];
                      if (Array.isArray(selectedApplication.ai_toolset) && selectedApplication.ai_toolset.length > 0) {
                        toolsList = selectedApplication.ai_toolset.map(t => typeof t === 'object' ? (t.name || t.tool || JSON.stringify(t)) : t);
                      }

                      if (skillsList.length === 0 && toolsList.length === 0) return null;

                      return (
                        <div style={{ marginTop: '24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
                          {skillsList.length > 0 && (
                            <div style={{ background: 'var(--faculty-bg)', borderRadius: '14px', padding: '24px', border: '1px solid var(--faculty-border)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', color: 'var(--faculty-text)' }}>
                                <Sparkles size={22} style={{ color: '#10b981' }} />
                                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>AI-Analyzed Skills</h3>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {skillsList.map((sk, idx) => (
                                  <span key={idx} style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981', padding: '6px 14px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                                    {sk}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {toolsList.length > 0 && (
                            <div style={{ background: 'var(--faculty-bg)', borderRadius: '14px', padding: '24px', border: '1px solid var(--faculty-border)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', color: 'var(--faculty-text)' }}>
                                <Terminal size={22} style={{ color: '#3b82f6' }} />
                                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>AI-Analyzed Tools & Tech Stack</h3>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {toolsList.map((tl, idx) => (
                                  <span key={idx} style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6', padding: '6px 14px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                                    {tl}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <>
                    <div className="faculty-page-heading" style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                      <div>
                        {onBackToDrives && (
                          <button
                            onClick={onBackToDrives}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: 'var(--faculty-muted)', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', padding: '0', marginBottom: '12px' }}
                          >
                            <ArrowLeft size={16} /> Back to Placement Drives
                          </button>
                        )}
                        <h2 style={{ margin: 0 }}>Applied Candidates</h2>
                        <p style={{ marginTop: '8px' }}>Review student applications for placement drives. Inspect their resume, GitHub profile, and AI-extracted skill sets to approve or reject candidates.</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', background: 'var(--faculty-bg)', borderRadius: '8px', padding: '4px', border: '1px solid var(--faculty-border)' }}>
                          <button
                            onClick={() => setApplicationsViewMode('grouped')}
                            style={{
                              padding: '8px 16px',
                              background: applicationsViewMode === 'grouped' ? '#fff' : 'transparent',
                              border: 'none',
                              borderRadius: '6px',
                              color: applicationsViewMode === 'grouped' ? 'var(--faculty-accent)' : 'var(--faculty-muted)',
                              fontWeight: applicationsViewMode === 'grouped' ? 700 : 600,
                              cursor: 'pointer',
                              boxShadow: applicationsViewMode === 'grouped' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
                              transition: 'all 0.2s'
                            }}
                          >
                            <Building2 size={16} style={{ display: 'inline-block', marginRight: '6px', verticalAlign: 'text-bottom' }} />
                            Grouped View
                          </button>
                          <button
                            onClick={() => setApplicationsViewMode('all')}
                            style={{
                              padding: '8px 16px',
                              background: applicationsViewMode === 'all' ? '#fff' : 'transparent',
                              border: 'none',
                              borderRadius: '6px',
                              color: applicationsViewMode === 'all' ? 'var(--faculty-accent)' : 'var(--faculty-muted)',
                              fontWeight: applicationsViewMode === 'all' ? 700 : 600,
                              cursor: 'pointer',
                              boxShadow: applicationsViewMode === 'all' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
                              transition: 'all 0.2s'
                            }}
                          >
                            <ListChecks size={16} style={{ display: 'inline-block', marginRight: '6px', verticalAlign: 'text-bottom' }} />
                            View All
                          </button>
                        </div>
                        
                        {handleSyncApplications && (
                          <SubFeatureGate feature="mantrika" sub="mantrika_application_sync">
                          <button
                            onClick={handleSyncApplications}
                            disabled={syncingApplications}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px',
                              background: syncingApplications ? '#e2e8f0' : '#eff6ff',
                              color: syncingApplications ? '#94a3b8' : '#2563eb',
                              border: '1px solid #bfdbfe', borderRadius: '8px',
                              fontWeight: 600, fontSize: '0.85rem', cursor: syncingApplications ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            <RefreshCw size={16} className={syncingApplications ? 'spin' : ''} />
                            {syncingApplications ? 'Syncing...' : 'Sync Status'}
                          </button>
                          </SubFeatureGate>
                        )}
                      </div>
                    </div>

                    <div>
                      {loadingApplied ? (
                        <div style={{ background: '#fff', border: '1px solid var(--faculty-border)', borderRadius: '12px', padding: '48px', textAlign: 'center', color: 'var(--faculty-muted)' }}>Loading submitted applications...</div>
                      ) : appliedCandidates.length === 0 ? (
                        <div style={{ background: '#fff', border: '1px solid var(--faculty-border)', borderRadius: '12px', textAlign: 'center', padding: '56px 20px' }}>
                          <CalendarCheck size={40} style={{ color: 'var(--faculty-muted)', marginBottom: '12px' }} />
                          <p style={{ color: 'var(--faculty-muted)', fontWeight: 600 }}>No applications submitted yet.</p>
                        </div>
                      ) : (
                        <div>
                          {applicationsViewMode === 'grouped' ? (
                            <div>
                          {!selectedAppliedCompany ? (
                            <div className="tpo-applied-table tpo-applied-company-table" style={{ border: '1px solid var(--faculty-border)', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ background: 'var(--faculty-bg)', borderBottom: '1px solid var(--faculty-border)' }}>
                                    <th style={{ padding: '14px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '800', color: 'var(--faculty-muted)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>COMPANY</th>
                                    <th style={{ padding: '14px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '800', color: 'var(--faculty-muted)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>TOTAL APPLICATIONS</th>
                                    <th style={{ padding: '14px 24px', textAlign: 'right', fontSize: '0.75rem', fontWeight: '800', color: 'var(--faculty-muted)', letterSpacing: '0.8px', textTransform: 'uppercase' }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries(
                                    appliedCandidates.reduce((acc, app) => {
                                      const comp = app.company_name || 'Corporate Partner';
                                      if (!acc[comp]) acc[comp] = [];
                                      acc[comp].push(app);
                                      return acc;
                                    }, {})
                                  ).map(([company, apps], idx, arr) => (
                                    <tr key={company} onClick={() => setSelectedAppliedCompany(company)} style={{ borderBottom: idx < arr.length - 1 ? '1px solid var(--faculty-border)' : 'none', cursor: 'pointer', transition: 'background 0.2s' }}
                                      onMouseEnter={e => e.currentTarget.style.background = 'var(--faculty-bg)'}
                                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                      <td style={{ padding: '16px 24px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                          <div style={{ background: 'linear-gradient(135deg, var(--faculty-accent), #2563eb)', width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                                            <Building2 size={20} />
                                          </div>
                                          <div style={{ fontSize: '1.05rem', color: 'var(--faculty-text)', fontWeight: 800 }}>
                                            {company}
                                          </div>
                                        </div>
                                      </td>
                                      <td style={{ padding: '16px 24px', fontSize: '0.95rem', color: 'var(--faculty-muted)', fontWeight: 600 }}>
                                        {apps.length} {apps.length === 1 ? 'Application' : 'Applications'}
                                      </td>
                                      <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                                        <button style={{ background: 'transparent', border: '1px solid var(--faculty-border)', padding: '6px 14px', borderRadius: '6px', color: 'var(--faculty-accent)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                          View Roles &rarr;
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : !selectedAppliedRole ? (
                            <div>
                              <button onClick={() => setSelectedAppliedCompany(null)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: 'var(--faculty-muted)', cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem', padding: '0 0 20px 0', transition: 'color 0.2s' }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--faculty-text)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--faculty-muted)'}
                              >
                                &larr; Back to Companies
                              </button>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid var(--faculty-border)' }}>
                                <div style={{ background: 'linear-gradient(135deg, var(--faculty-accent), #2563eb)', width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                                  <Building2 size={20} />
                                </div>
                                <h3 style={{ fontSize: '1.5rem', color: 'var(--faculty-text)', margin: 0, fontWeight: 800 }}>{selectedAppliedCompany} Roles</h3>
                              </div>
                              <div className="tpo-applied-table tpo-applied-role-table" style={{ border: '1px solid var(--faculty-border)', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ background: 'var(--faculty-bg)', borderBottom: '1px solid var(--faculty-border)' }}>
                                      <th style={{ padding: '14px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '800', color: 'var(--faculty-muted)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>JOB ROLE</th>
                                      <th style={{ padding: '14px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '800', color: 'var(--faculty-muted)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>TOTAL APPLICANTS</th>
                                      <th style={{ padding: '14px 24px', textAlign: 'right', fontSize: '0.75rem', fontWeight: '800', color: 'var(--faculty-muted)', letterSpacing: '0.8px', textTransform: 'uppercase' }}></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.entries(
                                      appliedCandidates.filter(a => (a.company_name || 'Corporate Partner') === selectedAppliedCompany).reduce((acc, app) => {
                                        const role = app.job_name || 'Unknown Role';
                                        if (!acc[role]) acc[role] = [];
                                        acc[role].push(app);
                                        return acc;
                                      }, {})
                                    ).map(([role, apps], idx, arr) => (
                                      <tr key={role} onClick={() => setSelectedAppliedRole(role)} style={{ borderBottom: idx < arr.length - 1 ? '1px solid var(--faculty-border)' : 'none', cursor: 'pointer', transition: 'background 0.2s' }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--faculty-bg)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                      >
                                        <td style={{ padding: '16px 24px' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                            <div style={{ background: 'var(--faculty-bg)', width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--faculty-accent)', flexShrink: 0 }}>
                                              <Briefcase size={20} />
                                            </div>
                                            <div style={{ fontSize: '1.05rem', color: 'var(--faculty-text)', fontWeight: 800 }}>
                                              {role}
                                            </div>
                                          </div>
                                        </td>
                                        <td style={{ padding: '16px 24px', fontSize: '0.95rem', color: 'var(--faculty-muted)', fontWeight: 600 }}>
                                          {apps.length} {apps.length === 1 ? 'Applicant' : 'Applicants'}
                                        </td>
                                        <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                                          <button style={{ background: 'transparent', border: '1px solid var(--faculty-border)', padding: '6px 14px', borderRadius: '6px', color: 'var(--faculty-accent)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            View Candidates &rarr;
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <button onClick={() => setSelectedAppliedRole(null)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: 'var(--faculty-muted)', cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem', padding: '0 0 20px 0', transition: 'color 0.2s' }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--faculty-text)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--faculty-muted)'}
                              >
                                &larr; Back to {selectedAppliedCompany} Roles
                              </button>
                              
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid var(--faculty-border)' }}>
                                <div style={{ background: 'var(--faculty-bg)', width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--faculty-accent)' }}>
                                  <Briefcase size={20} />
                                </div>
                                <div>
                                  <h3 style={{ fontSize: '1.5rem', color: 'var(--faculty-text)', margin: '0 0 4px 0', fontWeight: 800 }}>{selectedAppliedRole}</h3>
                                  <div style={{ fontSize: '0.9rem', color: 'var(--faculty-muted)', fontWeight: 600 }}>{selectedAppliedCompany}</div>
                                </div>
                              </div>

                              <CandidateRankingPanel
                                jobId={appliedCandidates.find(app => (app.company_name || 'Corporate Partner') === selectedAppliedCompany && (app.job_name || 'Unknown Role') === selectedAppliedRole)?.job_id}
                                roleName={selectedAppliedRole}
                              />

                              <div className="tpo-applied-table tpo-applied-candidate-table" style={{ border: '1px solid var(--faculty-border)', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ background: 'var(--faculty-bg)', borderBottom: '1px solid var(--faculty-border)' }}>
                                      {['CANDIDATE', 'BATCH & BRANCH', 'STATUS', 'APPLIED AT', 'ACTIONS'].map(col => (
                                        <th key={col} style={{ padding: '14px 20px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '800', color: 'var(--faculty-muted)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>{col}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {appliedCandidates
                                      .filter(a => (a.company_name || 'Corporate Partner') === selectedAppliedCompany && (a.job_name || 'Unknown Role') === selectedAppliedRole)
                                      .map((app, idx, arr) => {
                                      const name = app.student_name || 'Unknown';
                                      const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                                      const colors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4'];
                                      const color = colors[name.charCodeAt(0) % colors.length];
                                      return (
                                        <tr key={app.id} style={{ borderBottom: idx < arr.length - 1 ? '1px solid var(--faculty-border)' : 'none', transition: 'background 0.15s' }}
                                          onMouseEnter={e => e.currentTarget.style.background = 'var(--faculty-bg)'}
                                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                          <td style={{ padding: '16px 20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                              <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '800', fontSize: '0.9rem', flexShrink: 0 }}>{initials}</div>
                                              <div>
                                                <div style={{ fontWeight: '700', color: 'var(--faculty-text)', fontSize: '0.95rem', marginBottom: '2px' }}>{name}</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--faculty-muted)' }}>{app.email}</div>
                                              </div>
                                            </div>
                                          </td>
                                          <td style={{ padding: '16px 20px', fontSize: '0.9rem', color: 'var(--faculty-muted)' }}>
                                            <span style={{ fontWeight: 700, color: 'var(--faculty-text)' }}>{app.batch}</span> • {app.branch}
                                          </td>
                                          <td style={{ padding: '16px 20px' }}>
                                            <span style={{
                                              padding: '6px 14px',
                                              borderRadius: '20px',
                                              fontWeight: 800,
                                              fontSize: '0.75rem',
                                              ...getStatusStyle(app.status)
                                            }}>
                                              {formatStatusLabel(app.status)}
                                            </span>
                                          </td>
                                          <td style={{ padding: '16px 20px', fontSize: '0.85rem', color: 'var(--faculty-muted)' }}>
                                            {app.applied_at}
                                          </td>
                                          <td style={{ padding: '16px 20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                              <button
                                                onClick={() => setSelectedApplication(app)}
                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', border: '1px solid var(--faculty-border)', borderRadius: '8px', background: '#fff', color: 'var(--faculty-text)', fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}
                                                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--faculty-accent)'; e.currentTarget.style.color = 'var(--faculty-accent)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--faculty-border)'; e.currentTarget.style.color = 'var(--faculty-text)'; }}
                                              >
                                                Review
                                              </button>
                                              {app.status !== 'APPROVED' && app.status !== 'Approved' && app.status !== 'Remarked' && (
                                                <button
                                                  title="Approve"
                                                  onClick={() => handleUpdateAppStatus(app.id, 'APPROVED')}
                                                  style={{ padding: '8px 12px', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '8px', background: 'rgba(16, 185, 129, 0.12)', color: '#10b981', cursor: 'pointer', fontWeight: 800, fontSize: '0.85rem' }}
                                                >
                                                  ✓
                                                </button>
                                              )}
                                              {app.status !== 'APPROVED' && app.status !== 'Approved' && app.status !== 'REMARKED' && app.status !== 'Remarked' && (
                                                <button
                                                  title="Add Remarks"
                                                  onClick={() => openRemarksModal(app)}
                                                  style={{ padding: '8px 12px', border: '1px solid #bfdbfe', borderRadius: '8px', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontWeight: 800, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                >
                                                  <MessageSquare size={16} />
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                              <div style={{ background: '#fff', border: '1px solid var(--faculty-border)', borderRadius: '12px', padding: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', background: 'var(--faculty-bg)', borderRadius: '8px', padding: '8px 12px', flex: '1 1 250px' }}>
                                  <Search size={18} color="var(--faculty-muted)" style={{ marginRight: '8px', flexShrink: 0 }} />
                                  <input 
                                    type="text" 
                                    placeholder="Search by name or email..." 
                                    value={candidateSearch}
                                    onChange={e => setCandidateSearch(e.target.value)}
                                    style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', fontSize: '0.9rem' }}
                                  />
                                </div>
                                <select 
                                  value={candidateCompanyFilter}
                                  onChange={e => {
                                    setCandidateCompanyFilter(e.target.value);
                                    setCandidateRoleFilter('All');
                                  }}
                                  style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--faculty-border)', background: '#fff', color: 'var(--faculty-text)', outline: 'none', flex: '1 1 150px', cursor: 'pointer' }}
                                >
                                  <option value="All">All Companies</option>
                                  {Array.from(new Set(appliedCandidates.map(a => a.company_name || 'Corporate Partner'))).map(c => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </select>
                                <select 
                                  value={candidateRoleFilter}
                                  onChange={e => setCandidateRoleFilter(e.target.value)}
                                  style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--faculty-border)', background: '#fff', color: 'var(--faculty-text)', outline: 'none', flex: '1 1 150px', cursor: 'pointer' }}
                                >
                                  <option value="All">All Roles</option>
                                  {Array.from(new Set(
                                    appliedCandidates
                                      .filter(a => candidateCompanyFilter === 'All' || (a.company_name || 'Corporate Partner') === candidateCompanyFilter)
                                      .map(a => a.job_name || 'Unknown Role')
                                  )).map(r => (
                                    <option key={r} value={r}>{r}</option>
                                  ))}
                                </select>
                                <select 
                                  value={candidateStatusFilter}
                                  onChange={e => setCandidateStatusFilter(e.target.value)}
                                  style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--faculty-border)', background: '#fff', color: 'var(--faculty-text)', outline: 'none', flex: '1 1 150px', cursor: 'pointer' }}
                                >
                                  <option value="All">All Statuses</option>
                                  <option value="APPLIED">Applied</option>
                                  <option value="APPROVED">Approved</option>
                                  <option value="UNDER_REVIEW">Under Review</option>
                                  <option value="INTERVIEW">Interview</option>
                                  <option value="SHORTLISTED">Shortlisted</option>
                                  <option value="OFFERED">Offered</option>
                                  <option value="HIRED">Hired</option>
                                  <option value="OFFER_DECLINED">Offer Declined</option>
                                  <option value="OFFER_LAPSED">Offer Lapsed</option>
                                  <option value="REJECTED">Rejected</option>
                                  <option value="WITHDRAWN">Withdrawn</option>
                                </select>
                              </div>
                              
                              <div className="tpo-applied-table tpo-applied-all-table" style={{ border: '1px solid var(--faculty-border)', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ background: 'var(--faculty-bg)', borderBottom: '1px solid var(--faculty-border)' }}>
                                      {['CANDIDATE', 'COMPANY & ROLE', 'BATCH & BRANCH', 'STATUS', 'ACTIONS'].map(col => (
                                        <th key={col} style={{ padding: '14px 20px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '800', color: 'var(--faculty-muted)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>{col}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {appliedCandidates.filter(a => {
                                      if (candidateCompanyFilter !== 'All' && (a.company_name || 'Corporate Partner') !== candidateCompanyFilter) return false;
                                      if (candidateRoleFilter !== 'All' && (a.job_name || 'Unknown Role') !== candidateRoleFilter) return false;
                                      if (candidateStatusFilter !== 'All' && (a.status || 'APPLIED').toUpperCase() !== candidateStatusFilter.toUpperCase()) return false;
                                      if (candidateSearch) {
                                        const term = candidateSearch.toLowerCase();
                                        const name = (a.student_name || '').toLowerCase();
                                        const email = (a.email || '').toLowerCase();
                                        if (!name.includes(term) && !email.includes(term)) return false;
                                      }
                                      return true;
                                    }).map((app, idx, arr) => {
                                      const name = app.student_name || 'Unknown';
                                      const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                                      const colors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4'];
                                      const color = colors[name.charCodeAt(0) % colors.length];
                                      return (
                                        <tr key={app.id} style={{ borderBottom: idx < arr.length - 1 ? '1px solid var(--faculty-border)' : 'none', transition: 'background 0.15s' }}
                                          onMouseEnter={e => e.currentTarget.style.background = 'var(--faculty-bg)'}
                                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                          <td style={{ padding: '16px 20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                              <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '800', fontSize: '0.9rem', flexShrink: 0 }}>{initials}</div>
                                              <div>
                                                <div style={{ fontWeight: '700', color: 'var(--faculty-text)', fontSize: '0.95rem', marginBottom: '2px' }}>{name}</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--faculty-muted)' }}>{app.email}</div>
                                              </div>
                                            </div>
                                          </td>
                                          <td style={{ padding: '16px 20px', fontSize: '0.85rem', color: 'var(--faculty-text)' }}>
                                            <div style={{ fontWeight: 700, marginBottom: '2px' }}>{app.company_name || 'Corporate Partner'}</div>
                                            <div style={{ color: 'var(--faculty-muted)' }}>{app.job_name || 'Unknown Role'}</div>
                                          </td>
                                          <td style={{ padding: '16px 20px', fontSize: '0.9rem', color: 'var(--faculty-muted)' }}>
                                            <span style={{ fontWeight: 700, color: 'var(--faculty-text)' }}>{app.batch}</span> • {app.branch}
                                          </td>
                                          <td style={{ padding: '16px 20px' }}>
                                            <span style={{
                                              padding: '6px 14px',
                                              borderRadius: '20px',
                                              fontWeight: 800,
                                              fontSize: '0.75rem',
                                              ...getStatusStyle(app.status)
                                            }}>
                                              {formatStatusLabel(app.status)}
                                            </span>
                                          </td>
                                          <td style={{ padding: '16px 20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                              <button
                                                title="Review Profile"
                                                onClick={() => setSelectedApplication(app)}
                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', border: '1px solid var(--faculty-border)', borderRadius: '8px', background: '#fff', color: 'var(--faculty-text)', fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}
                                                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--faculty-accent)'; e.currentTarget.style.color = 'var(--faculty-accent)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--faculty-border)'; e.currentTarget.style.color = 'var(--faculty-text)'; }}
                                              >
                                                Review
                                              </button>
                                              {(!app.status || app.status.toUpperCase() === 'APPLIED') && (
                                                <button
                                                  title="Approve"
                                                  onClick={() => handleUpdateAppStatus(app.id, 'APPROVED')}
                                                  style={{ padding: '8px 12px', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '8px', background: 'rgba(16, 185, 129, 0.12)', color: '#10b981', cursor: 'pointer', fontWeight: 800, fontSize: '0.85rem' }}
                                                >
                                                  ✓
                                                </button>
                                              )}
                                              {(!app.status || app.status.toUpperCase() === 'APPLIED') && (
                                                <button
                                                  title="Add Remarks"
                                                  onClick={() => openRemarksModal(app)}
                                                  style={{ padding: '8px 12px', border: '1px solid #bfdbfe', borderRadius: '8px', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontWeight: 800, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                >
                                                  <MessageSquare size={16} />
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

  );
};

export default AppliedCandidatesModule;
